import React, { useState } from 'react';
import { 
  Layers, 
  CheckCircle2, 
  Clock, 
  FileEdit, 
  Quote, 
  RefreshCw, 
  ExternalLink, 
  Home, 
  Landmark, 
  GraduationCap, 
  FileText, 
  Activity, 
  ArrowRight,
  ShieldCheck,
  ChevronRight,
  Info
} from 'lucide-react';
import './DashboardOverview.css';

function DashboardOverview({ apiBase, stats, onRefresh, onNavigateTab, user }) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (onRefresh) {
        await onRefresh();
      }
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 600);
    }
  };

  const PAGE_DIRECTORY = [
    {
      id: 'cms-hero',
      pageTitle: 'Homepage',
      desc: 'Hero banner video, quick links, highlights kegiatan, berita & countdown acara',
      sectionsCount: 5,
      routeId: 'cms-hero'
    },
    {
      id: 'cms-about-hero',
      pageTitle: 'About Us',
      desc: 'Pesan Kepala Sekolah, Visi-Misi & Nilai, Filosofi, Sejarah, serta Jajaran Pimpinan',
      sectionsCount: 6,
      routeId: 'cms-about-hero'
    },
    {
      id: 'cms-education-hero',
      pageTitle: 'Education Program',
      desc: 'Kurikulum & program jenjang Preschool, Kindergarten, Primary, hingga Secondary',
      sectionsCount: 7,
      routeId: 'cms-education-hero'
    },
    {
      id: 'cms-sl-hero',
      pageTitle: 'Student Life',
      desc: 'Ekstrakurikuler, house system, pembentukan karakter, dan wellbeing siswa',
      sectionsCount: 7,
      routeId: 'cms-sl-hero'
    },
    {
      id: 'cms-fac-hero',
      pageTitle: 'Facilities',
      desc: 'Fasilitas kampus, teknologi pembelajaran, keamanan, dan galeri sarana',
      sectionsCount: 6,
      routeId: 'cms-fac-hero'
    },
    {
      id: 'cms-news-hero',
      pageTitle: 'News & Updates',
      desc: 'Siaran pers resmi, artikel prestasi siswa, dan publikasi kegiatan sekolah',
      sectionsCount: 3,
      routeId: 'cms-news-hero'
    },
    {
      id: 'cms-par-hero',
      pageTitle: 'Parents',
      desc: 'Komunitas orang tua (PaLC), buku panduan, portal informasi, dan saluran komunikasi',
      sectionsCount: 5,
      routeId: 'cms-par-hero'
    },
    {
      id: 'cms-car-hero',
      pageTitle: 'Careers',
      desc: 'Filosofi perekrutan guru/staf, lowongan kerja aktif, dan peluang magang',
      sectionsCount: 4,
      routeId: 'cms-car-hero'
    },
    {
      id: 'cms-con-hero',
      pageTitle: 'Contact Us',
      desc: 'Informasi alamat kampus, kontak WhatsApp/Telepon, FAQ, kemitraan, dan testimoni',
      sectionsCount: 6,
      routeId: 'cms-con-hero'
    }
  ];

  return (
    <div className="wa-overview-container">
      {/* 1. WELCOME BANNER HEADER */}
      <section className="wa-welcome-banner">
        <div className="wa-welcome-left">
          <div className="wa-welcome-role-badge">
            <ShieldCheck size={14} />
            <span>Website Administrator</span>
          </div>
          <h1 className="wa-welcome-title">
            Selamat Datang, {user?.full_name || 'Website Admin'}
          </h1>
          <p className="wa-welcome-subtitle">
            Portal Manajemen Konten Resmi Permata Harapan Secondary School. Kelola informasi halaman, kurasi testimoni, dan pantau status publikasi secara terpusat.
          </p>
        </div>
        <div className="wa-welcome-actions">
          <button 
            type="button" 
            className="wa-btn-refresh" 
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            title="Muat ulang data statistik"
          >
            <RefreshCw size={15} className={isRefreshing ? 'spin' : ''} />
            <span>{isRefreshing ? 'Memperbarui...' : 'Refresh Data'}</span>
          </button>

          <a 
            href="https://permataharapansecondary.sch.id" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="wa-btn-live-site"
            title="Buka website publik di tab baru"
          >
            <ExternalLink size={15} />
            <span>Lihat Website Publik</span>
          </a>
        </div>
      </section>

      {/* 2. CONDITIONAL ACTION ALERTS */}
      {stats.pendingTestimonials > 0 && (
        <section className="wa-alert-banner warning">
          <div className="wa-alert-icon-box">
            <Quote size={20} />
          </div>
          <div className="wa-alert-text-wrap">
            <div className="wa-alert-heading">
              <span className="wa-alert-pill warning">Perlu Tindakan Admin</span>
              <strong>{stats.pendingTestimonials} Testimonial Baru Menunggu Review</strong>
            </div>
            <p className="wa-alert-desc">
              Ada pengajuan testimonial baru dari orang tua siswa yang belum dikurasi. Silakan tinjau dan setujui untuk dapat diajukan ke Principal.
            </p>
          </div>
          <button 
            type="button" 
            className="wa-alert-action-btn warning"
            onClick={() => onNavigateTab && onNavigateTab('testimonials-management')}
          >
            <span>Review Testimonial</span>
            <ArrowRight size={14} />
          </button>
        </section>
      )}

      {stats.pendingApproval > 0 && (
        <section className="wa-alert-banner info">
          <div className="wa-alert-icon-box">
            <Clock size={20} />
          </div>
          <div className="wa-alert-text-wrap">
            <div className="wa-alert-heading">
              <span className="wa-alert-pill info">Dalam Peninjauan</span>
              <strong>{stats.pendingApproval} Perubahan Menunggu Persetujuan Principal</strong>
            </div>
            <p className="wa-alert-desc">
              Perubahan konten telah berhasil diserahkan ke antrean verifikasi Principal. Konten akan otomatis aktif di website publik setelah disetujui.
            </p>
          </div>
          <button
            type="button"
            className="wa-alert-action-btn"
            onClick={() => onNavigateTab && onNavigateTab('approval-status')}
          >
            <span>Lihat Status</span>
            <ChevronRight size={14} />
          </button>
        </section>
      )}

      {/* 3. EXECUTIVE STATS METRICS GRID */}
      <section className="wa-stats-grid">
        {/* Card 1: Total Konten */}
        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap purple">
            <Layers size={22} />
          </div>
          <div className="wa-stat-body">
            <span className="wa-stat-label">Total Seksi Konten</span>
            <div className="wa-stat-number">{stats.totalContent || 38}</div>
            <span className="wa-stat-hint">Tersebar di 10 modul website</span>
          </div>
        </div>

        {/* Card 2: Published */}
        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap green">
            <CheckCircle2 size={22} />
          </div>
          <div className="wa-stat-body">
            <span className="wa-stat-label">Konten Published</span>
            <div className="wa-stat-number">{stats.publishedContent || 38}</div>
            <span className="wa-stat-hint">Aktif tayang di website publik</span>
          </div>
        </div>

        {/* Card 3: Pending Approval */}
        <div 
          className="wa-stat-card clickable"
          onClick={() => onNavigateTab && onNavigateTab('approval-status')}
          title="Buka Halaman Status & Riwayat Approval"
        >
          <div className="wa-stat-icon-wrap amber">
            <Clock size={22} />
          </div>
          <div className="wa-stat-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span className="wa-stat-label">Menunggu Approval</span>
              <ChevronRight size={14} className="wa-stat-arrow" />
            </div>
            <div className="wa-stat-number">{stats.pendingApproval || 0}</div>
            <span className="wa-stat-hint">Sedang diverifikasi Principal</span>
          </div>
        </div>

        {/* Card 4: Draft */}
        <div className="wa-stat-card">
          <div className="wa-stat-icon-wrap slate">
            <FileEdit size={22} />
          </div>
          <div className="wa-stat-body">
            <span className="wa-stat-label">Draft Konten</span>
            <div className="wa-stat-number">{stats.draftContent || 0}</div>
            <span className="wa-stat-hint">Perubahan disimpan lokal</span>
          </div>
        </div>

        {/* Card 5: Testimonials Pending */}
        <div 
          className="wa-stat-card clickable"
          onClick={() => onNavigateTab && onNavigateTab('testimonials-management')}
          title="Klik untuk membuka kelola testimonial"
        >
          <div className="wa-stat-icon-wrap pink">
            <Quote size={22} />
          </div>
          <div className="wa-stat-body">
            <div className="wa-stat-label-row">
              <span className="wa-stat-label">Testimonial Pending</span>
              <ChevronRight size={14} className="wa-stat-arrow" />
            </div>
            <div className="wa-stat-number">{stats.pendingTestimonials || 0}</div>
            <span className="wa-stat-hint">Total: {stats.totalTestimonials || 6} testimoni masuk</span>
          </div>
        </div>
      </section>

      {/* 4. FAST EDITOR ACCESS HUB */}
      <section className="wa-section-card">
        <div className="wa-section-header">
          <div>
            <h2 className="wa-section-title">Akses Cepat Pengeditan Halaman</h2>
            <p className="wa-section-subtitle">
              Pilih modul halaman di bawah untuk langsung menuju formulir pengeditan konten CMS
            </p>
          </div>
        </div>

        <div className="wa-quick-actions-grid">
          {/* Action 1: Homepage */}
          <div 
            className="wa-action-card"
            onClick={() => onNavigateTab && onNavigateTab('cms-hero')}
          >
            <div className="wa-action-icon home">
              <Home size={20} />
            </div>
            <div className="wa-action-content">
              <h3 className="wa-action-title">Homepage</h3>
              <p className="wa-action-desc">Hero banner, video, sambutan, countdown, dan footer sekolah.</p>
            </div>
            <div className="wa-action-footer">
              <span>Buka Editor</span>
              <ArrowRight size={13} />
            </div>
          </div>

          {/* Action 2: About Us */}
          <div 
            className="wa-action-card"
            onClick={() => onNavigateTab && onNavigateTab('cms-about-hero')}
          >
            <div className="wa-action-icon about">
              <Landmark size={20} />
            </div>
            <div className="wa-action-content">
              <h3 className="wa-action-title">About Us</h3>
              <p className="wa-action-desc">Pesan Principal, Visi-Misi, Sejarah, Nilai, dan Jajaran Pimpinan.</p>
            </div>
            <div className="wa-action-footer">
              <span>Buka Editor</span>
              <ArrowRight size={13} />
            </div>
          </div>

          {/* Action 3: Education Program */}
          <div 
            className="wa-action-card"
            onClick={() => onNavigateTab && onNavigateTab('cms-education-hero')}
          >
            <div className="wa-action-icon edu">
              <GraduationCap size={20} />
            </div>
            <div className="wa-action-content">
              <h3 className="wa-action-title">Education Program</h3>
              <p className="wa-action-desc">Kurikulum Preschool, Kindergarten, Primary, dan Secondary.</p>
            </div>
            <div className="wa-action-footer">
              <span>Buka Editor</span>
              <ArrowRight size={13} />
            </div>
          </div>

          {/* Admissions page content is now managed in the Admin Admissions portal */}

          {/* Action 5: Student Life */}
          <div 
            className="wa-action-card"
            onClick={() => onNavigateTab && onNavigateTab('cms-sl-hero')}
          >
            <div className="wa-action-icon student">
              <Activity size={20} />
            </div>
            <div className="wa-action-content">
              <h3 className="wa-action-title">Student Life</h3>
              <p className="wa-action-desc">Ekstrakurikuler, house system, wellbeing, dan galeri kegiatan.</p>
            </div>
            <div className="wa-action-footer">
              <span>Buka Editor</span>
              <ArrowRight size={13} />
            </div>
          </div>

          {/* Action 6: Testimonials Management */}
          <div 
            className="wa-action-card"
            onClick={() => onNavigateTab && onNavigateTab('testimonials-management')}
          >
            <div className="wa-action-icon test">
              <Quote size={20} />
            </div>
            <div className="wa-action-content">
              <h3 className="wa-action-title">Testimonials Review</h3>
              <p className="wa-action-desc">Moderasi dan kurasi ulasan publik dari orang tua siswa dan alumni.</p>
            </div>
            <div className="wa-action-footer">
              <span>Buka Review</span>
              <ArrowRight size={13} />
            </div>
          </div>
        </div>
      </section>

      {/* 5. DIRECTORY TABLE OF ALL PAGES */}
      <section className="wa-section-card">
        <div className="wa-section-header">
          <div>
            <h2 className="wa-section-title">Direktori Halaman Website</h2>
            <p className="wa-section-subtitle">
              Daftar seluruh halaman publik yang terkelola melalui Content Management System
            </p>
          </div>
        </div>

        <div className="wa-table-responsive">
          <table className="wa-directory-table">
            <thead>
              <tr>
                <th>Modul Halaman</th>
                <th>Cakupan Konten</th>
                <th className="text-center">Jumlah Seksi</th>
                <th className="text-center">Status Publik</th>
                <th className="text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {PAGE_DIRECTORY.map((item, index) => (
                <tr key={item.id}>
                  <td className="wa-td-title">
                    <span className="wa-dir-number">{index + 1}</span>
                    <span className="wa-dir-title">{item.pageTitle}</span>
                  </td>
                  <td className="wa-td-desc">
                    {item.desc}
                  </td>
                  <td className="text-center">
                    <span className="wa-badge-sections">
                      {item.sectionsCount} Seksi
                    </span>
                  </td>
                  <td className="text-center">
                    <span className="wa-badge-status-published">
                      <span className="status-dot-mini" />
                      Aktif
                    </span>
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="wa-btn-table-edit"
                      onClick={() => onNavigateTab && onNavigateTab(item.routeId)}
                      title={`Buka form editor ${item.pageTitle}`}
                    >
                      <span>Buka Form</span>
                      <ArrowRight size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 6. CMS WORKFLOW GUIDE */}
      <section className="wa-guide-card">
        <div className="wa-guide-header">
          <Info size={18} className="wa-guide-info-icon" />
          <h3 className="wa-guide-title">Panduan Alur Pengelolaan Konten (CMS Workflow)</h3>
        </div>
        <div className="wa-guide-steps-grid">
          <div className="wa-guide-step">
            <div className="wa-step-badge">1</div>
            <div className="wa-step-body">
              <strong>Pilih & Perbarui</strong>
              <p>Pilih seksi halaman yang diinginkan melalui sidebar Content Management atau menu cepat, lalu perbarui teks maupun media gambar.</p>
            </div>
          </div>

          <div className="wa-guide-step">
            <div className="wa-step-badge">2</div>
            <div className="wa-step-body">
              <strong>Ajukan Approval</strong>
              <p>Klik tombol Ajukan Approval agar perubahan masuk ke antrean verifikasi Kepala Sekolah (Principal).</p>
            </div>
          </div>

          <div className="wa-guide-step">
            <div className="wa-step-badge">3</div>
            <div className="wa-step-body">
              <strong>Terbit Otomatis</strong>
              <p>Setelah disetujui Principal, konten akan langsung aktif secara instan pada website publik tanpa perlu redeploy kode.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default DashboardOverview;
